import React, { useCallback, useRef, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Textarea from '@amzn/meridian/textarea';
import Icon from '@amzn/meridian/icon';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import Toggle from '@amzn/meridian/toggle';
import Tooltip from '@amzn/meridian/tooltip';
import uploadSmall from '@amzn/meridian-tokens/base/icon/upload-small';
import downloadSmall from '@amzn/meridian-tokens/base/icon/download-small';
import checkCircleTokens from '@amzn/meridian-tokens/base/icon/check-circle';
import alertKnockoutTokens from '@amzn/meridian-tokens/base/icon/alert-knockout';
import trashTokens from '@amzn/meridian-tokens/base/icon/trash';
import { useTranslation } from 'src/arb-wrapper';
import { GTI_REGEX, GTI_LIST_MAX, GTI_LIST_MIN, GTI_CSV_MAX } from 'src/constants';
import type { FormMeridianInputProps, FormRhfControllerProps } from './types';
import Loader from '@amzn/meridian/loader';
import { useGtiValidation } from 'src/hooks/useGtiValidation';

/**
 * Re-prioritizes GTIs by placing valid ones first, then limiting to max count
 * @returns Object with newValue (string) and shouldWarn (boolean)
 */
export function reprioritizeGtis (
  gtisWithTitles: string[],
  gtisWithoutTitles: string[],
  maxCount: number
): { newValue: string; shouldWarn: boolean } {
  const prioritizedGtis = [...gtisWithTitles, ...gtisWithoutTitles];
  const limitedGtis = prioritizedGtis.slice(0, maxCount);
  const newValue = limitedGtis.join('\n');
  const shouldWarn = gtisWithTitles.length > maxCount;

  return { newValue, shouldWarn };
}

interface FormGtiInputProps extends FormMeridianInputProps, FormRhfControllerProps {
  validator?: {
    fn: () => boolean;
    message?: string;
  };
  onValidateModeChange?: (isValidateMode: boolean) => void;
  gtiTitleMap?: Map<string, { title?: string; coverImageUrl?: string }>;
  isFetching?: boolean;
  showTitleColumn?: boolean;
  showInvalidFilteredWarning?: boolean;
  requireMinimum?: boolean;
  maxGtis?: number;
  requireCoverImage?: boolean;
}

export const FormGtiInput: React.FC<FormGtiInputProps> = (props) => {
  const { control, name, validator, onValidateModeChange, gtiTitleMap, isFetching, showTitleColumn = false, showInvalidFilteredWarning = false, requireMinimum = true, maxGtis = GTI_LIST_MAX, requireCoverImage = true, ...meridianProps } = props;
  const { t } = useTranslation();
  const { trigger } = useFormContext();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isValidateMode, setIsValidateMode] = useState(false);
  const [csvWarning, setCsvWarning] = useState<string | null>(null);
  const [hasInitialized, setHasInitialized] = useState(false);

  const { field, fieldState } = useController({
    control,
    name,
    rules: {
      required: meridianProps.required
        ? {
            value: true,
            message: t('form_validation_gti_required'),
          }
        : undefined,
      validate: () => {
        // Custom validator takes precedence
        if (validator && !validator.fn()) {
          return validator.message ?? 'Validation failed';
        }

        if (!isValidateMode) {
          if (gtiCount > maxGtis) {
            return t('form_gti_list_manual_exceeds_limit', { count: gtiCount, max: maxGtis });
          }
          return false; // Invalid but no specific error message
        }

        // Block validation while fetching or if gtiTitleMap not ready in validate mode
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        if (isFetching || (isValidateMode && gtiCount > 0 && !gtiTitleMap)) {
          return t('form_gti_list_loading_titles');
        }

        // Check minimum valid GTI count (must have at least GTI_LIST_MIN valid GTIs)
        if (requireMinimum && gtiCount > 0 && validCount < GTI_LIST_MIN) {
          return t('form_gti_list_min_valid_required', { min: GTI_LIST_MIN });
        }

        // Block save when GTIs exist but none are valid
        if (gtiCount > 0 && validCount === 0) {
          return t('form_validation_gti_required');
        }

        return true;
      },
    },
  });

  // GTI Validation
  const {
    uniqueGtis,
    validationResults,
    gtiCount,
    validCount,
    isGtiValid,
    parseGtiList,
  } = useGtiValidation({
    value: field.value || '',
    gtiTitleMap,
    isFetching,
    requireCoverImage,
  });

  // Store valid GTIs in a hidden field for submission
  const validGtisFieldName = `${name}_valid`;
  const { setValue } = useFormContext();

  React.useEffect(() => {
    // Update hidden field with only valid GTIs whenever validation changes
    const validGtisOnly = uniqueGtis.filter(gti => isGtiValid(gti));
    setValue(validGtisFieldName, validGtisOnly.join('\n'), { shouldValidate: false });
  }, [uniqueGtis, isGtiValid, setValue, validGtisFieldName]);

  // Trigger revalidation when validation dependencies change
  React.useEffect(() => {
    trigger(name);
  }, [isValidateMode, isFetching, validCount, gtiCount, gtiTitleMap, name, trigger]);

  // Track gtiTitleMap size to detect actual data changes
  const prevGtiTitleMapSizeRef = useRef<number>(0);

  React.useEffect(() => {
    // Re-prioritize whenever we have title data and GTIs in validate mode
    const currentGtiCount = uniqueGtis.length;
    const currentMapSize = gtiTitleMap?.size ?? 0;

    // Skip if map size hasn't changed
    if (currentMapSize === prevGtiTitleMapSizeRef.current && prevGtiTitleMapSizeRef.current > 0) {
      return;
    }
    prevGtiTitleMapSizeRef.current = currentMapSize;

    if (isValidateMode && gtiTitleMap && currentGtiCount >= maxGtis) {
      const gtisWithTitles = uniqueGtis.filter(gti => isGtiValid(gti));
      const gtisWithoutTitles = uniqueGtis.filter(gti => !isGtiValid(gti));

      if (gtisWithTitles.length === 0) {
        return;
      }

      const result = reprioritizeGtis(gtisWithTitles, gtisWithoutTitles, maxGtis);
      const currentValue = String(field.value ?? '');

      if (result.newValue !== currentValue) {
        if (result.shouldWarn) {
          setCsvWarning(t('form_gti_list_valid_exceeds', { max: maxGtis }));
        }

        field.onChange(result.newValue);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gtiTitleMap, isValidateMode]);

  // Handle CSV file download
  const handleDownloadCsv = useCallback(() => {
    const gtis = parseGtiList(String(field.value ?? ''));
    const csvContent = gtis.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'gti-list.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }, [field.value, parseGtiList]);

  // Handle CSV file upload
  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onerror = () => {
      const errorMsg = t('form_gti_list_csv_read_error');
      setCsvWarning(errorMsg);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };

    reader.onload = (e) => {
      const text = e.target?.result as string;
      if (!text) {
        const errorMsg = t('form_gti_list_csv_read_error');
        setCsvWarning(errorMsg);
        return;
      }

      // Parse CSV and extract valid GTIs (limit to GTI_CSV_MAX)
      const lines = text.split(/\r?\n/);
      const validGtis: string[] = [];
      let totalValidGtis = 0;

      lines.forEach(line => {
        const trimmed = line.trim();
        if (GTI_REGEX.test(trimmed)) {
          totalValidGtis++;
          if (validGtis.length < GTI_CSV_MAX) {
            validGtis.push(trimmed);
          }
        }
      });

      // Check if we have any valid GTIs
      if (validGtis.length === 0) {
        setCsvWarning(t('form_gti_list_csv_no_valid_gtis'));
        return;
      }

      // Show warning if CSV exceeded the limit
      if (totalValidGtis > GTI_CSV_MAX) {
        setCsvWarning(t('form_gti_list_csv_limit_exceeded', { count: totalValidGtis, max: GTI_CSV_MAX }));
      } else {
        // Clear warning - will be set after fetch completes if needed
        setCsvWarning(null);
      }

      // Load GTIs (up to GTI_CSV_MAX) - will be prioritized after fetch
      const newValue = validGtis.join('\n');
      field.onChange(newValue);

      // Switch to validate mode after CSV is loaded
      setIsValidateMode(true);
      if (onValidateModeChange) {
        onValidateModeChange(true);
      }
    };

    reader.readAsText(file);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [field, t, onValidateModeChange]);

  // Handle deleting a GTI from the list
  const handleDeleteGti = useCallback((gtiToDelete: string) => {
    const currentGtis = parseGtiList(String(field.value ?? ''));
    const updatedGtis = currentGtis.filter(gti => gti !== gtiToDelete);

    // Update field value
    const newValue = updatedGtis.join('\n');
    field.onChange(newValue);
    // Clear CSV warning when user manually deletes GTIs
    setCsvWarning(null);
  }, [field, parseGtiList]);

  // Initialize validate mode if there's existing GTI data
  React.useEffect(() => {
    if (!hasInitialized && field.value) {
      if (gtiCount > 0) {
        setIsValidateMode(true);
        if (onValidateModeChange) {
          onValidateModeChange(true);
        }
      }
      setHasInitialized(true);
    }
  }, [hasInitialized, field.value, gtiCount, onValidateModeChange]);

  // Handle toggle between edit and validate modes
  const handleToggle = useCallback(() => {
    const newValidateMode = !isValidateMode;

    setIsValidateMode(newValidateMode);

    // Notify parent of mode change
    if (onValidateModeChange) {
      onValidateModeChange(newValidateMode);
    }
  }, [isValidateMode, onValidateModeChange]);

  // Render GTI list with validation indicators and delete buttons
  const renderValidatedList = () => {
    return (
      <Column spacing="200">
        <div style={{ height: '300px', overflowY: 'auto' }}>
          <Table headerRows={1} showDividers spacing="small">
            <TableRow>
              <TableCell alignmentHorizontal="center">{t('form_global_valid')}</TableCell>
              <TableCell alignmentHorizontal="center">{t('form_global_thumbnail')}</TableCell>
              <TableCell>{t('form_global_gti')}</TableCell>
              {showTitleColumn && <TableCell>{t('form_global_title')}</TableCell>}
              <TableCell alignmentHorizontal="center">{t('form_global_actions')}</TableCell>
            </TableRow>

            {validationResults.slice(0, maxGtis).map((result, index) => {
              const isValidFormat = result.isValid;
              const hasTitleData = Boolean(gtiTitleMap?.has(result.gti) && gtiTitleMap.get(result.gti)?.coverImageUrl);
              const isFullyValid = isGtiValid(result.gti);

              const getTooltipMessage = () => {
                if (!isValidFormat) {
                  return t('form_gti_list_invalid_format');
                } else if (!isFetching && !hasTitleData) {
                  return t('form_gti_list_no_title_data');
                }
                return '';
              };

              return (
                <TableRow key={index}>
                  <TableCell alignmentHorizontal="center">
                    {isFetching
                      ? (
                      <Loader size="small" />
                        )
                      : getTooltipMessage()
                        ? (
                      <Tooltip position="top" title={getTooltipMessage()}>
                        <div data-testid={`gti-validation-tooltip-${index}`} style={{ transform: 'scale(0.7)', display: 'inline-flex' }}>
                          <Icon
                            color={isFullyValid ? 'success' : 'error'}
                            tokens={isFullyValid ? checkCircleTokens : alertKnockoutTokens}
                          />
                        </div>
                      </Tooltip>
                          )
                        : (
                      <div data-testid={`gti-validation-tooltip-${index}`} style={{ transform: 'scale(0.7)', display: 'inline-flex' }}>
                        <Icon
                          color={isFullyValid ? 'success' : 'error'}
                          tokens={isFullyValid ? checkCircleTokens : alertKnockoutTokens}
                        />
                      </div>
                          )}
                  </TableCell>
                  <TableCell alignmentHorizontal="center">
                    {gtiTitleMap?.has(result.gti) && gtiTitleMap.get(result.gti)?.coverImageUrl
                      ? (
                        <img
                          alt={gtiTitleMap.get(result.gti)?.title ?? result.gti}
                          src={gtiTitleMap.get(result.gti)?.coverImageUrl ?? ''}
                          style={{ maxWidth: '100px' }}
                          title={gtiTitleMap.get(result.gti)?.title ?? result.gti}
                        />
                        )
                      : null}
                  </TableCell>
                  <TableCell>
                    <Column spacing="100">
                      {gtiTitleMap?.has(result.gti) && gtiTitleMap.get(result.gti)?.title && (
                        <Text color="primary" type="b200">
                          {gtiTitleMap.get(result.gti)?.title}
                        </Text>
                      )}
                      <Text color="secondary" type="b100">
                        {result.gti}
                      </Text>
                    </Column>
                  </TableCell>
                  {showTitleColumn && (
                    <TableCell>
                      <Text type="b200">
                        {gtiTitleMap?.get(result.gti)?.title ?? '--'}
                      </Text>
                    </TableCell>
                  )}
                  <TableCell alignmentHorizontal="center">
                    <Button
                      data-testid={`delete-gti-${index}`}
                      size="small"
                      type="icon"
                      onClick={() => { handleDeleteGti(result.gti); }}
                    >
                      <Icon tokens={trashTokens} />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </Table>
        </div>
        {showInvalidFilteredWarning && !isFetching && validCount > 0 && validCount < totalCount && (
          <Alert size="medium" type="warning">
            {t('form_gti_list_invalid_filtered_warning', { count: totalCount - validCount })}
          </Alert>
        )}
        {fieldState.error && (
          <Alert
            size="medium"
            type={fieldState.error.message === t('form_gti_list_loading_titles') ? 'theme' : 'error'}
          >
            {fieldState.error.message}
          </Alert>
        )}
        {csvWarning && (
          <Alert size="medium" type="theme">
            {csvWarning}
          </Alert>
        )}
      </Column>
    );
  };

  const totalCount = validationResults.length;

  return (
    <Column spacing="100" width="100%">
      <input
        accept=".csv,.txt"
        ref={fileInputRef}
        style={{ display: 'none' }}
        type="file"
        onChange={handleFileUpload}
      />
      <Row alignmentHorizontal="justify" alignmentVertical="center">
        <div>
          <Row alignmentVertical="center" spacing="200">
            <Text type="b200">{meridianProps.label}</Text>
            {isFetching && (
              <Loader size="small" />
            )}
          </Row>
          {gtiCount > 0 && !isValidateMode && (
            <Text color="secondary" type="b100">
              {t('form_gti_list_count', { count: gtiCount, max: maxGtis })}
              {gtiCount > maxGtis && ` (max ${maxGtis})`}
            </Text>
          )}
          {isValidateMode && totalCount > 0 && !isFetching && (
            <Text color="secondary" type="b100">
              {t('form_gti_list_valid_count', { valid: validCount, total: totalCount })}
              {totalCount > maxGtis && ` (max ${maxGtis})`}
            </Text>
          )}
        </div>
        <Row alignmentVertical="center" spacing="200">
          <Row alignmentVertical="center" spacing="100">
            <Text type="b100">{t('form_gti_list_upload_button')}</Text>
            <Button
              aria-label={t('form_gti_list_upload_button')}
              data-testid="upload-csv-button"
              type="icon"
              onClick={() => fileInputRef.current?.click()}
            >
              <Icon tokens={uploadSmall} />
            </Button>
            <Text type="b100">{t('form_gti_list_download_button')}</Text>
            <Button
              aria-label={t('form_gti_list_download_button')}
              data-testid="download-csv-button"
              disabled={gtiCount === 0}
              type="icon"
              onClick={handleDownloadCsv}
            >
              <Icon tokens={downloadSmall} />
            </Button>
          </Row>
          <Row alignmentVertical="center" spacing="200">
            <Text type="b100">{t('form_gti_list_preview_button')}</Text>
            <Toggle
              checked={isValidateMode}
              data-testid="toggle-preview"
              disabled={!isValidateMode && (!field.value || gtiCount === 0)}
              onChange={handleToggle}
            />
          </Row>
        </Row>
      </Row>

      {!isValidateMode
        ? (
          <>
            <div style={{ height: '300px', overflow: 'auto' }}>
              <Textarea
                data-testid="gti-list-textarea"
                placeholder={t('form_gti_list_placeholder')}
                rows={10}
                value={Array.isArray(field.value) ? field.value.join('\n') : (field.value || '')}
                onBlur={(e) => {
                  field.onBlur();
                  // Auto-switch to preview mode if there are GTIs
                  if (gtiCount > 0) {
                    setIsValidateMode(true);
                    if (onValidateModeChange) {
                      onValidateModeChange(true);
                    }
                  }
                }}
                onChange={(e) => {
                  field.onChange(e);
                  // Clear CSV warning when user manually types
                  setCsvWarning(null);
                }}
              />
            </div>
            {csvWarning && (
              <Alert size="medium" type="theme">
                {csvWarning}
              </Alert>
            )}
            {fieldState.error?.message && (
              <Alert size="medium" type="theme">
                {fieldState.error.message}
              </Alert>
            )}
            <Text color="error" type="b100">
              {t('form_gti_list_switch_to_validate_mode')}
            </Text>
          </>
          )
        : (
            renderValidatedList()
          )}

    </Column>
  );
};
